// Client domains (issue #83): what counts as a domain the console can take on, and which DNS records carry email.
// Shared by the console Worker (validation) and its UI (hints and highlighting).

/** Our own platform domain: its subdomains are never a client's domain. */
export const PLATFORM_DOMAIN = 'digimasi.com'

/**
 * Two-label public suffixes a client domain may sit under (`example.co.id`). Not the full public suffix list: the
 * common ones for our clients (Indonesia first) and the region. A domain under an unlisted one is read as a subdomain.
 */
const SECOND_LEVEL_SUFFIXES = new Set(
  Object.entries({
    id: ['co', 'or', 'ac', 'sch', 'web', 'my', 'biz', 'go', 'net', 'mil', 'ponpes', 'desa'],
    uk: ['co', 'org', 'me', 'ltd', 'plc', 'ac', 'gov'],
    au: ['com', 'net', 'org', 'edu', 'gov', 'id'],
    sg: ['com', 'net', 'org', 'edu', 'gov'],
    my: ['com', 'net', 'org', 'edu', 'gov'],
    jp: ['co', 'ne', 'or', 'ac'],
    nz: ['co', 'net', 'org'],
    cn: ['com', 'net', 'org', 'gov', 'edu'],
    hk: ['com', 'net', 'org', 'edu'],
    tw: ['com', 'net', 'org'],
    th: ['co', 'in', 'or', 'ac'],
    ph: ['com', 'net', 'org'],
    vn: ['com', 'net'],
    in: ['co', 'net', 'org'],
    za: ['co'],
    br: ['com'],
  }).flatMap(([tld, slds]) => slds.map((s) => `${s}.${tld}`)),
)

const LABEL = /^(?!-)[a-z0-9-]{1,63}(?<!-)$/
const TLD = /^(?:[a-z]{2,63}|xn--[a-z0-9-]{1,59})$/

// Fixed English messages, translated by the UI (SITE_SERVER_MESSAGE_KEYS).
export const DOMAIN_INVALID = 'Enter a domain such as example.com'
export const DOMAIN_SUBDOMAIN = 'Enter the main domain (such as example.com), not a subdomain'
export const DOMAIN_PLATFORM = 'Addresses under digimasi.com are ours: enter the client’s own domain'

/** What staff typed (`https://www.Example.com/`, `example.com.`) as a bare lowercase host. */
export const cleanHost = (input: string) =>
  input
    .trim()
    .toLowerCase()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
    .replace(/[/?#:].*$/, '')
    .replace(/\.$/, '')

/** The registrable domain staff mean, or why it is not one. A leading `www.` is dropped. */
export function parseApexDomain(input: string): { ok: true; domain: string } | { ok: false; reason: string } {
  const host = cleanHost(input).replace(/^www\./, '')
  const labels = host.split('.')
  if (host.length > 253 || labels.length < 2 || !labels.every((l) => LABEL.test(l)) || !TLD.test(labels.at(-1)!)) return { ok: false, reason: DOMAIN_INVALID }
  if (host === PLATFORM_DOMAIN || host.endsWith(`.${PLATFORM_DOMAIN}`)) return { ok: false, reason: DOMAIN_PLATFORM }
  const lastTwo = labels.slice(-2).join('.')
  if (labels.length === 2) return SECOND_LEVEL_SUFFIXES.has(lastTwo) ? { ok: false, reason: DOMAIN_INVALID } : { ok: true, domain: host }
  if (labels.length === 3 && SECOND_LEVEL_SUFFIXES.has(lastTwo)) return { ok: true, domain: host }
  return { ok: false, reason: DOMAIN_SUBDOMAIN }
}

/** One DNS label (a subdomain such as `cms`). */
export const isHostLabel = (v: string) => LABEL.test(v)

/** A hostname a record may point at (CNAME/MX target). Underscore labels are allowed (`_dmarc`, DKIM selectors). */
export const isHostname = (v: string) => {
  const host = v.replace(/\.$/, '')
  return host.length > 0 && host.length <= 253 && host.split('.').length >= 2 && host.split('.').every((l) => /^(?!-)[a-z0-9_-]{1,63}(?<!-)$/i.test(l))
}

export type EmailRecordKind = 'mx' | 'spf' | 'dkim' | 'dmarc' | 'autodiscover'

/** Whether a DNS record is part of the client's email (so it must survive the nameserver switch), and which part. */
export function emailRecordKind(r: { type: string; name: string; content: string }): EmailRecordKind | null {
  const name = r.name.toLowerCase()
  const content = r.content.replace(/^"|"$/g, '').trim().toLowerCase()
  if (r.type === 'MX') return 'mx'
  if (r.type === 'TXT') {
    if (content.startsWith('v=spf1')) return 'spf'
    if (name.startsWith('_dmarc.') || content.startsWith('v=dmarc1')) return 'dmarc'
    if (name.includes('._domainkey.') || content.startsWith('v=dkim1')) return 'dkim'
  }
  if (r.type === 'CNAME') {
    if (name.includes('._domainkey.')) return 'dkim'
    if (name.startsWith('autodiscover.') || name.startsWith('autoconfig.')) return 'autodiscover'
  }
  return null
}
